import type { BrowserNotificationPreferences } from '@scrambl/shared';
import { isMainWindow, isTauri } from '@/lib/utils/tauri';
import { createLogger } from '@/lib/logger';

const logger = createLogger('services/browserNotificationService');

export interface NotificationAction {
  id: string;
  title: string;
}

export interface BrowserNotificationOptions {
  title: string;
  body: string;
  icon?: string;
  image?: string;
  badge?: string;
  tag?: string;
  data?: unknown;
  requireInteraction?: boolean;
  silent?: boolean;
  // Actions for desktop notifications
  actions?: NotificationAction[];
}

export class BrowserNotificationService {
  private static instance: BrowserNotificationService;
  private preferences: BrowserNotificationPreferences | null = null;
  private isTabActive = true;
  private tauriActionListenerSetup = false;
  private pendingNotificationData: Map<string, unknown> = new Map();
  private tauriPermissionStatus: NotificationPermission = 'default';
  private tauriPermissionRequested = false;

  private constructor() {
    this.setupVisibilityChangeListener();
    void this.setupTauriActionListener().catch((error) => {
      logger.debug('[BrowserNotificationService] Failed to set up Tauri action listener:', error);
    });
    void this.refreshTauriPermissionStatus().catch((error) => {
      logger.debug('[BrowserNotificationService] Failed to refresh Tauri permission state:', error);
    });
  }

  static getInstance(): BrowserNotificationService {
    if (!BrowserNotificationService.instance) {
      BrowserNotificationService.instance = new BrowserNotificationService();
    }
    return BrowserNotificationService.instance;
  }

  private setupVisibilityChangeListener(): void {
    document.addEventListener('visibilitychange', () => {
      this.isTabActive = !document.hidden;
    });
  }

  /**
   * Setup Tauri notification action listener for handling notification clicks
   * Note: This requires the notification plugin to be configured with action support in Rust.
   * If not available, notifications will still work but click handling will be limited.
   */
  private async setupTauriActionListener(): Promise<void> {
    if (this.tauriActionListenerSetup || !isTauri()) return;

    try {
      const { onAction } = await import('@tauri-apps/plugin-notification');

      // onAction may not be available in all builds (requires specific Rust plugin setup)
      // Wrap in try-catch to prevent unhandled promise rejection
      try {
        await onAction((notification) => {
          // Focus the window when notification is clicked
          window.focus();

          // Get the stored data for this notification
          const notificationId = notification.id?.toString();
          if (notificationId) {
            const data = this.pendingNotificationData.get(notificationId);
            if (data) {
              this.handleNotificationClick(data);
              this.pendingNotificationData.delete(notificationId);
            }
          }

          // Handle action-specific behavior
          // The actionId property exists at runtime but may not be in types
          const actionId = (notification as { actionId?: string }).actionId;
          if (actionId) {
            this.handleNotificationAction(actionId, notification);
          }
        });

        this.tauriActionListenerSetup = true;
      } catch (error) {
        // onAction command not available - notifications will still work
        // but click actions won't be handled through the plugin
        logger.debug('[BrowserNotificationService] Notification action listener not available:', error);
        this.tauriActionListenerSetup = true;
      }
    } catch (error) {
      // Plugin import failed - likely not in Tauri environment
      logger.debug('[BrowserNotificationService] Notification action listener not available:', error);
    }
  }

  /**
   * Handle notification action button clicks
   */
  private handleNotificationAction(actionId: string, notification: unknown): void {
    switch (actionId) {
      case 'view':
        // View action - handled by default click behavior
        break;
      case 'dismiss':
        // Dismiss action - notification is already dismissed
        break;
      case 'mark_read':
        // Emit event for marking notification as read
        window.dispatchEvent(new CustomEvent('scrambl:notification-mark-read', {
          detail: { notificationId: notification }
        }));
        break;
      default:
    }
  }

  /**
   * Check if browser notifications are supported
   * In Tauri, native notifications are always supported
   */
  isSupported(): boolean {
    if (isTauri()) {
      return true;
    }
    return 'Notification' in window;
  }

  /**
   * Get current permission status
   * Uses the native plugin permission state in Tauri
   */
  getPermissionStatus(): NotificationPermission {
    if (!this.isSupported()) {
      return 'denied';
    }
    if (isTauri()) {
      return this.tauriPermissionStatus;
    }
    return Notification.permission;
  }

  /**
   * Request permission for browser notifications
   * Requests native permission in Tauri
   */
  async requestPermission(): Promise<NotificationPermission> {
    if (!this.isSupported()) {
      return 'denied';
    }

    if (isTauri()) {
      return this.ensureTauriPermission();
    }

    if (Notification.permission === 'default') {
      const permission = await Notification.requestPermission();
      return permission;
    }

    return Notification.permission;
  }

  /**
   * Update user preferences for browser notifications
   */
  updatePreferences(preferences: BrowserNotificationPreferences): void {
    this.preferences = preferences;
  }

  /**
   * Check if we should show notification based on preferences and current state
   */
  private shouldShowNotification(notificationType?: string): boolean {
    if (!this.preferences?.enabled) {
      return false;
    }

    if (!isTauri() && this.getPermissionStatus() !== 'granted') {
      return false;
    }

    // Check if we should show when tab is active/inactive
    if (this.isTabActive && this.preferences.showWhenTabActive === false) {
      return false;
    }

    if (!this.isTabActive && this.preferences.showWhenTabInactive === false) {
      return false;
    }

    // Check if specific notification type is enabled
    if (notificationType && this.preferences.enabledTypes) {
      const typeKey = notificationType as keyof typeof this.preferences.enabledTypes;
      if (this.preferences.enabledTypes[typeKey] === false) {
        return false;
      }
    }

    return true;
  }

  /**
   * Show a browser notification (or native Tauri notification)
   */
  async showNotification(
    options: BrowserNotificationOptions,
    notificationType?: string
  ): Promise<Notification | null> {
    if (!this.shouldShowNotification(notificationType)) {
      return null;
    }

    if (isTauri()) {
      const isPrimaryWindow = await isMainWindow();
      if (!isPrimaryWindow) {
        return null;
      }
    }

    try {
      // Use Tauri native notifications in desktop app
      if (isTauri()) {
        return await this.showTauriNotification(options);
      }

      // Use browser notifications
      const notification = new Notification(options.title, {
        body: options.body,
        icon: options.icon || '/favicon.ico',
        badge: options.badge,
        tag: options.tag,
        data: options.data,
        requireInteraction: options.requireInteraction || false,
        silent: options.silent || !this.preferences?.playSound,
      });

      // Auto-dismiss if configured
      if (this.preferences?.autoDismissMs && this.preferences.autoDismissMs > 0) {
        setTimeout(() => {
          notification.close();
        }, this.preferences.autoDismissMs);
      }

      // Handle notification click
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus(); // Focus the window
        notification.close();
        
        // Handle custom data if provided
        if (options.data) {
          this.handleNotificationClick(options.data);
        }
      };

      return notification;
    } catch (error) {
      logger.warn('Failed to show browser notification:', error);
      return null;
    }
  }

  /**
   * Show a native Tauri notification with enhanced features
   */
  private async showTauriNotification(
    options: BrowserNotificationOptions
  ): Promise<Notification | null> {
    try {
      // Check and request permission if needed (on macOS)
      const permission = await this.ensureTauriPermission();
      if (permission !== 'granted') {
        logger.warn('Tauri notification permission denied');
        return null;
      }

      const desktopSettings = this.preferences?.desktopSettings;

      // Generate a unique ID for tracking notification data
      const notificationId = `${options.tag || 'notification'}-${Date.now()}`;
      const notificationNumericId =
        parseInt(notificationId.replace(/\D/g, '').slice(-9), 10) || Date.now();

      // Store the notification data for click handling
      if (options.data) {
        this.pendingNotificationData.set(notificationNumericId.toString(), options.data);

        // Clean up after 5 minutes to prevent memory leaks
        setTimeout(() => {
          this.pendingNotificationData.delete(notificationNumericId.toString());
        }, 5 * 60 * 1000);
      }

      // Build notification options
      const notificationOptions: {
        id?: number;
        title: string;
        body?: string;
        icon?: string;
        sound?: string;
        group?: string;
      } = {
        id: notificationNumericId,
        title: options.title,
        body: options.body,
        icon: this.resolveTauriIcon(options.icon),
      };

      // Add sound if enabled
      if (this.preferences?.playSound !== false) {
        const soundName = desktopSettings?.soundName;
        if (soundName && soundName !== 'default') {
          notificationOptions.sound = soundName;
        }
      }

      // Group notifications if enabled
      if (desktopSettings?.groupNotifications && options.tag) {
        notificationOptions.group = options.tag.split('-')[0]; // Group by notification type
      }

      // Note: Action buttons are mobile-only in Tauri's notification plugin
      // On desktop (macOS/Windows), the onAction callback is not supported
      // so we skip adding action buttons to avoid confusion

      // Send the native notification
      const { sendNotification } = await import('@tauri-apps/plugin-notification');
      await sendNotification(notificationOptions);

      // Update badge count if enabled
      if (desktopSettings?.showBadgeCount !== false) {
        this.updateBadgeCount();
      }

      // Return null since Tauri doesn't return a Notification object
      // The notification is handled by the OS
      return null;
    } catch (error) {
      logger.warn('Failed to show Tauri notification:', error);
      return null;
    }
  }

  /**
   * Update the dock/taskbar badge count
   */
  private async updateBadgeCount(): Promise<void> {
    if (!isTauri()) return;

    try {
      const { invoke } = await import('@tauri-apps/api/core');
      // This will call a Rust command we'll add to update the badge
      await invoke('update_badge_count');
    } catch (error) {
      // Badge update is optional, don't log errors
    }
  }

  /**
   * Set a specific badge count (called from notification store)
   */
  async setBadgeCount(count: number): Promise<void> {
    if (!isTauri()) return;

    try {
      const { invoke } = await import('@tauri-apps/api/core');
      await invoke('set_badge_count', { count });
    } catch (error) {
      logger.warn('Failed to set badge count:', error);
    }
  }

  /**
   * Clear the badge count
   */
  async clearBadgeCount(): Promise<void> {
    await this.setBadgeCount(0);
  }

  /**
   * Show a local-only reminder notification (desktop). Unlike showNotification,
   * this deliberately bypasses the user's task-notification preferences and the
   * in-app notification store/popover — it is a transient, client-side prompt
   * (e.g. "your meeting is starting — record it?") that should never be listed
   * alongside server notifications.
   */
  async showLocalReminder(options: {
    title: string;
    body: string;
    tag?: string;
    data?: unknown;
  }): Promise<void> {
    try {
      if (isTauri()) {
        const isPrimaryWindow = await isMainWindow();
        if (!isPrimaryWindow) return;
        await this.showTauriNotification({
          title: options.title,
          body: options.body,
          tag: options.tag,
          data: options.data,
        });
        return;
      }

      if (this.getPermissionStatus() !== 'granted') return;
      const notification = new Notification(options.title, {
        body: options.body,
        icon: '/favicon.ico',
        tag: options.tag,
        data: options.data,
      });
      notification.onclick = (event) => {
        event.preventDefault();
        window.focus();
        notification.close();
        if (options.data) this.handleNotificationClick(options.data);
      };
    } catch (error) {
      logger.warn('Failed to show local reminder notification:', error);
    }
  }

  /**
   * Handle notification click actions
   */
  private handleNotificationClick(data: unknown): void {
    if (typeof data === 'object' && data !== null) {
      const notificationData = data as Record<string, unknown>;

      // Local meeting-record reminders: dispatch a DOM event the desktop
      // MeetingReminderController listens for (keeps recording-domain logic
      // out of this service). Handled here so both the Tauri onAction path and
      // the web Notification.onclick path funnel through the same place.
      if (notificationData.type === 'meeting-record') {
        window.dispatchEvent(
          new CustomEvent('scrambl:meeting-record', { detail: notificationData }),
        );
        return;
      }
      if (notificationData.type === 'meeting-record-stop') {
        window.dispatchEvent(
          new CustomEvent('scrambl:meeting-record-stop', {
            detail: notificationData,
          }),
        );
        return;
      }

      // Navigate to specific pages based on notification type
      if (notificationData.url) {
        window.location.href = notificationData.url as string;
      } else if (notificationData.taskId) {
        // Navigate to task
        window.location.href = `/tasks/${notificationData.taskId}`;
      } else if (notificationData.projectId) {
        // Navigate to project
        window.location.href = `/projects/${notificationData.projectId}`;
      }
    }
  }

  /**
   * Show notification for task-related events
   */
  async showTaskNotification(
    title: string,
    body: string,
    taskId?: string,
    projectId?: string,
    organizationSlug?: string
  ): Promise<Notification | null> {
    const basePath = organizationSlug ? `/${organizationSlug}` : '';
    const url = taskId ? `${basePath}/tasks/${taskId}` : undefined;

    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: `task-${taskId}`,
      data: {
        type: 'task',
        taskId,
        projectId,
        url,
      },
    }, 'tasks');
  }

  /**
   * Show notification for comment-related events
   */
  async showCommentNotification(
    title: string,
    body: string,
    taskId?: string,
    threadId?: string,
    organizationSlug?: string,
    entityUrl?: string
  ): Promise<Notification | null> {
    const basePath = organizationSlug ? `/${organizationSlug}` : '';
    const url = entityUrl ?? (taskId ? `${basePath}/tasks/${taskId}` : undefined);

    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: `comment-${threadId || taskId}`,
      data: {
        type: 'comment',
        taskId,
        threadId,
        url,
      },
    }, 'comments');
  }

  /**
   * Show notification for mention events
   */
  async showMentionNotification(
    title: string,
    body: string,
    taskId?: string,
    threadId?: string,
    organizationSlug?: string,
    entityUrl?: string
  ): Promise<Notification | null> {
    const basePath = organizationSlug ? `/${organizationSlug}` : '';
    const url = entityUrl ?? (taskId ? `${basePath}/tasks/${taskId}` : undefined);

    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: `mention-${threadId || taskId}`,
      data: {
        type: 'mention',
        taskId,
        threadId,
        url,
      },
      requireInteraction: true, // Mentions are important
    }, 'mentions');
  }

  /**
   * Show notification for assignment events
   */
  async showAssignmentNotification(
    title: string,
    body: string,
    taskId?: string,
    projectId?: string,
    organizationSlug?: string
  ): Promise<Notification | null> {
    const basePath = organizationSlug ? `/${organizationSlug}` : '';
    const url = taskId ? `${basePath}/tasks/${taskId}` : undefined;

    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: `assignment-${taskId}`,
      data: {
        type: 'assignment',
        taskId,
        projectId,
        url,
      },
    }, 'assignments');
  }

  /**
   * Show notification for deadline events
   */
  async showDeadlineNotification(
    title: string,
    body: string,
    taskId?: string,
    projectId?: string,
    organizationSlug?: string
  ): Promise<Notification | null> {
    const basePath = organizationSlug ? `/${organizationSlug}` : '';
    const url = taskId ? `${basePath}/tasks/${taskId}` : undefined;

    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: `deadline-${taskId}`,
      data: {
        type: 'deadline',
        taskId,
        projectId,
        url,
      },
      requireInteraction: true, // Deadlines are important
    }, 'deadlines');
  }

  /**
   * Show notification for system events
   */
  async showSystemNotification(
    title: string,
    body: string,
    url?: string
  ): Promise<Notification | null> {
    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: 'system',
      data: {
        type: 'system',
        url,
      },
    }, 'system');
  }

  /**
   * Show notification for thread message events
   * @param threadType - thread type metadata for analytics/debugging
   */
  async showThreadMessageNotification(
    title: string,
    body: string,
    threadId: string,
    organizationSlug?: string,
    threadType?: 'DISCUSSION' | 'DIRECT_MESSAGE'
  ): Promise<Notification | null> {
    // Build URL with thread param for deep linking
    // Use discussions tab for deep linking.
    const url = organizationSlug
      ? `/${organizationSlug}/threads?tab=discussions&thread=${threadId}`
      : `/threads?tab=discussions&thread=${threadId}`;

    return this.showNotification({
      title,
      body,
      icon: '/favicon.ico',
      tag: `thread-message-${threadId}`,
      data: {
        type: 'thread_message',
        threadId,
        threadType,
        url,
      },
    }, 'comments'); // Use 'comments' type for notification preferences
  }

  /**
   * Clear all notifications with a specific tag
   */
  clearNotifications(tag: string): void {
  }

  /**
   * Test browser notification - for debugging
   */
  async testNotification(): Promise<void> {
    if (isTauri()) {
      await this.showTauriNotification({
        title: 'Test Notification',
        body: 'This is a test notification from Scrambl desktop',
        icon: '/icon.png',
      });
      return;
    }
    if (this.getPermissionStatus() === 'granted') {
      try {
        const notification = new Notification('Test Notification', {
          body: 'This is a test browser notification from Scrambl',
          icon: '/favicon.ico',
        });
        
        setTimeout(() => notification.close(), 5000);
      } catch (error) {
        logger.error('Test notification failed:', error);
      }
    }
    // When permission is not granted, skip test notification silently
  }

  /**
   * Get default preferences
   */
  static getDefaultPreferences(): BrowserNotificationPreferences {
    return {
      enabled: false,
      userDisabled: false,
      permissionRequested: false,
      permissionStatus: 'default',
      showWhenTabActive: false,
      showWhenTabInactive: true,
      playSound: true,
      autoDismissMs: 8000, // 8 seconds
      enabledTypes: {
        tasks: true,
        comments: true,
        mentions: true,
        assignments: true,
        deadlines: true,
        system: true,
      },
      desktopSettings: {
        showBadgeCount: true,
        soundName: 'default',
        enableActions: true,
        persistNotifications: false,
        groupNotifications: true,
      },
    };
  }

  /**
   * Check if running in desktop mode
   */
  isDesktopApp(): boolean {
    return isTauri();
  }

  /**
   * Get desktop-specific settings
   */
  getDesktopSettings() {
    return this.preferences?.desktopSettings || BrowserNotificationService.getDefaultPreferences().desktopSettings;
  }

  private resolveTauriIcon(icon?: string): string | undefined {
    if (!icon) return undefined;
    if (icon.startsWith('asset://') || icon.startsWith('file://') || icon.startsWith('http')) {
      return icon;
    }
    if (icon.startsWith('/')) {
      return `asset://${icon}`;
    }
    return `asset:///${icon}`;
  }

  private async refreshTauriPermissionStatus(): Promise<void> {
    if (!isTauri()) return;

    try {
      const { isPermissionGranted } = await import('@tauri-apps/plugin-notification');
      const permissionGranted = await isPermissionGranted();
      if (permissionGranted) {
        this.tauriPermissionStatus = 'granted';
      } else if (this.tauriPermissionRequested) {
        this.tauriPermissionStatus = 'denied';
      }
    } catch (error) {
      logger.debug('[BrowserNotificationService] Failed to read Tauri permission state:', error);
    }
  }

  private async ensureTauriPermission(): Promise<NotificationPermission> {
    if (!isTauri()) {
      return this.getPermissionStatus();
    }

    try {
      const { isPermissionGranted, requestPermission } = await import('@tauri-apps/plugin-notification');

      const permissionGranted = await isPermissionGranted();
      if (permissionGranted) {
        this.tauriPermissionStatus = 'granted';
        return 'granted';
      }

      if (this.tauriPermissionRequested) {
        this.tauriPermissionStatus = 'denied';
        return 'denied';
      }

      const permission = await requestPermission();
      this.tauriPermissionRequested = true;
      this.tauriPermissionStatus = permission;
      return permission;
    } catch (error) {
      logger.warn('[BrowserNotificationService] Failed to request Tauri notification permission:', error);
      this.tauriPermissionStatus = 'denied';
      return 'denied';
    }
  }
}

// Export singleton instance
export const browserNotificationService = BrowserNotificationService.getInstance();
